import React from 'react'
import * as stores from '../stores/appStore'
import * as types from '../types'

interface AddPersonModalProps {
  isOpen: boolean
  onClose: () => void
}

const AddPersonModal: React.FC<AddPersonModalProps> = ({ isOpen, onClose }) => {
  const { addPerson, groups } = stores.useAppStore()

  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    calendarUrls: '',
    selectedGroups: [] as string[],
    color: '#3b82f6'
  })

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // yellow
    '#f97316', // orange
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.firstName || !formData.calendarUrls) {
      return
    }

    setIsSubmitting(true)

    try {
      // Parse calendar URLs
      const urls = formData.calendarUrls
        .split('\n')
        .map(url => url.trim())
        .filter(url => url.length > 0)

      // Create calendars from URLs
      const calendars: types.Calendar[] = urls.map(url => ({
        id: crypto.randomUUID(),
        personId: '', // Will be set when person is created
        url,
        provider: 'calendly', // Default to calendly, could be detected from URL
        enabled: true,
        name: undefined
      }))

      // Create person
      await addPerson({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        color: formData.color,
        groups: formData.selectedGroups,
        calendars
      })

      // Reset form and close
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        calendarUrls: '',
        selectedGroups: [],
        color: '#3b82f6'
      })
      onClose()
    } catch (error) {
      console.error('Failed to add person:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-card rounded-lg max-w-md w-full p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Add New Person</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Avatar Upload - Placeholder */}
            <div className="flex justify-center">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: formData.color }}
              >
                {formData.firstName[0] || '👤'}
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  First Name*
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Calendar Links */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Calendar Links*
              </label>
              <textarea
                value={formData.calendarUrls}
                onChange={(e) => setFormData({ ...formData, calendarUrls: e.target.value })}
                placeholder="Paste Calendly, Cal.com, or other calendar links (one per line):"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                required
              />
            </div>

            {/* Groups */}
            {groups.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Groups (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {groups.map(group => (
                    <label key={group.id} className="flex items-center">
                      <input
                        type="checkbox"
                        value={group.id}
                        checked={formData.selectedGroups.includes(group.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              selectedGroups: [...formData.selectedGroups, group.id]
                            })
                          } else {
                            setFormData({
                              ...formData,
                              selectedGroups: formData.selectedGroups.filter(id => id !== group.id)
                            })
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        {group.emoji} {group.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Color
              </label>
              <div className="flex gap-2">
                {colors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color ? 'scale-125 ring-2 ring-offset-2 ring-primary-500' : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Person'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddPersonModal